import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconArrowRight, IconBrandMeta, IconLockCheck } from '@tabler/icons-react';
import { STUDIO_AUTOMATIONS_CONNECT, STUDIO_HOME } from '../../../../enums/routes';
import ConnectScene from '../ConnectScene';
import './styles.scss';

// Why an account is not connected yet, and what connecting it means. Three cases:
// `profile`: the Studio has no profile yet, so the @ comes first and leads to the onboarding.
// `connect`: the profile exists without the official connection.
// `permissions`: connected, but the Direct's permission is missing.
export default function ConnectHero({ studio: s, variant = 'connect', context = null, embedded = false }) {
  const { t } = useTranslation('studioAutomations');
  const handle = s.profile?.handle;
  const missing = variant === 'permissions' ? s.automations.connection?.missing_capabilities ?? [] : [];
  const copy = {
    profile: { title: t('heroProfileTitle'), lead: t('heroLead'), action: t('heroProfileAction'), steps: [[t('heroProfileStep1'), t('heroProfileStep1Hint')], [t('heroProfileStep2'), t('heroProfileStep2Hint')]] },
    connect: { title: handle ? t('heroConnectTitle', { handle }) : t('heroProfileTitle'), lead: t('heroLead'), action: t('heroAction'), steps: [[t('heroConnectStep1'), t('heroConnectStep1Hint')], [t('heroConnectStep2'), t('heroConnectStep2Hint')]] },
    permissions: { title: t('inboxMissingTitle'), lead: t('inboxMissingText'), action: t('connectGrant'), steps: [] },
  }[variant];
  const go = () => s.navigate(variant === 'profile' ? STUDIO_HOME : STUDIO_AUTOMATIONS_CONNECT);
  const Title = embedded ? Text.h3 : Text.h2;
  return <section className={`studio-connect-hero${embedded ? ' is-embedded' : ''}`}>
    <div className="studio-connect-hero__inner">
      <div className="studio-connect-hero__copy">
        <Text.caption contrast={6} left={<IconBrandMeta />}>{t('heroEyebrow')}</Text.caption>
        <Title weight={400} contrast={9}>{copy.title}</Title>
        <Text.p contrast={7}>{copy.lead}</Text.p>
        {context && <Text.p contrast={7}>{context}</Text.p>}
        {copy.steps.length > 0 && <ol className="studio-connect-hero__steps">
          {copy.steps.map(([title, hint], index) => <li key={title}>
            <span className="studio-connect-hero__step-mark" aria-hidden="true">{index + 1}</span>
            <span className="studio-connect-hero__step-text">
              <Text.label contrast={9}>{title}</Text.label>
              <Text.caption contrast={6}>{hint}</Text.caption>
            </span>
          </li>)}
        </ol>}
        <div className="studio-connect-hero__action">
          <Button variant="rounded" color="inverse" size="large" endIcon={<IconArrowRight />} preventLoading onClick={go}><Text.label>{copy.action}</Text.label></Button>
        </div>
        <div className="studio-connect-hero__assurance">
          <IconLockCheck />
          <Text.caption contrast={6}>{t('connectAssurance')}</Text.caption>
        </div>
      </div>
      <div className="studio-connect-hero__art" role="img" aria-label={t('sceneLabel')}>
        <ConnectScene key={missing.join()} handle={handle} picture={s.profile?.picture} missing={missing} />
      </div>
    </div>
  </section>;
}
